/**
 * Split payment ("Multiple Payment Modes") helpers for the Add Fee dialog.
 *
 * UI model: each split row's `pay` is a NET amount — rows must sum to
 * (fund.pay − discount). On submit the rows are converted back to GROSS
 * amounts so the backend still receives sum(splits.pay) === fund.pay.
 */

export interface PaymentSplitRow {
  id: string;
  paymentMode: string;
  /** NET line amount typed by the user; null = empty input (placeholder). */
  pay: number | null;
  tax: number;
  /** Display-only tax for INCLUDING_TAX modes (tax stays 0). */
  includedTax?: number;
  lineDiscount?: number;
  /** (pay − lineDiscount) + tax when WITH_TAX, else net. */
  lineTotal?: number;
  taxRate: number;
  taxType: string;
  transactionNumber?: string;
  feeReceiptImageUrl?: string;
}

export interface BranchPaymentLike {
  type?: string;
  name?: string;
  taxSettings?: { taxCalculationType?: string; taxRate?: number };
}

export interface RecalculateRowTaxOptions {
  isDiscountFormulaReverse?: boolean;
  fundPay?: number;
  fundDiscount?: number;
}

/** Split money values are whole numbers (save / tax / SRB / fee list). */
const roundInt = (n: number): number => Math.round(Number(n) || 0);

export function createEmptySplitRow(defaultMode = ''): PaymentSplitRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    paymentMode: defaultMode,
    pay: null,
    tax: 0,
    taxRate: 0,
    taxType: 'NO_TAX',
  };
}

/**
 * Selecting a payment mode updates TAX SETTINGS ONLY — never the amount.
 * Mirrors onPaymentModeChange(): mode value is "Type-Name", matched by name.
 */
export function applyTaxSettingsToRow(
  row: PaymentSplitRow,
  branchPayments: unknown,
  mode: string,
): void {
  row.paymentMode = mode;
  row.taxRate = 0;
  row.taxType = 'NO_TAX';
  const extractedName = (mode || '').split('-')[1];
  if (Array.isArray(branchPayments)) {
    for (const payment of branchPayments as BranchPaymentLike[]) {
      if (payment.name === extractedName && payment.taxSettings) {
        row.taxType = payment.taxSettings.taxCalculationType || 'NO_TAX';
        row.taxRate = Number(payment.taxSettings.taxRate) || 0;
      }
    }
  }
}

/**
 * Per-line tax, mirroring the single-mode Add Fee rules.
 * `row.pay` here must be the GROSS line amount; `lineDiscount` its share of
 * the total discount.
 */
export function recalculateRowTax(
  row: PaymentSplitRow,
  lineDiscount = 0,
  options: RecalculateRowTaxOptions = {},
): void {
  const pay = Number(row.pay) || 0;
  const discount = Number(lineDiscount) || 0;
  const fundPay = Number(options.fundPay) || 0;
  const fundDiscount = Number(options.fundDiscount) || 0;
  const isReverse = options.isDiscountFormulaReverse === true;

  row.includedTax = 0;
  if (row.taxType === 'NO_TAX' || !row.taxRate) {
    row.tax = 0;
    return;
  }
  if (row.taxType === 'INCLUDING_TAX') {
    // Tax is inside the price — record 0, expose includedTax for display only.
    row.tax = 0;
    if (pay > 0 && discount < pay) {
      row.includedTax = roundInt(((pay - discount) * row.taxRate) / 100);
    }
    return;
  }
  // WITH_TAX
  if (fundPay > 0 && fundDiscount >= fundPay) {
    // Fully discounted fee → no tax (matches single-mode discount == pay rule).
    row.tax = 0;
    return;
  }
  if (isReverse) {
    row.tax = roundInt((pay * row.taxRate) / 100);
    return;
  }
  row.tax = discount >= pay ? 0 : roundInt(((pay - discount) * row.taxRate) / 100);
}

/** Line subtotal: (gross pay − line discount) + tax when WITH_TAX, else net. */
export function calculateSplitRowTotal(
  row: PaymentSplitRow,
  lineDiscount = 0,
): number {
  const pay = Number(row.pay) || 0;
  const discount = Number(lineDiscount) || 0;
  const net = Math.max(0, pay - discount);
  if (row.taxType === 'WITH_TAX') {
    return roundInt(net + (Number(row.tax) || 0));
  }
  return roundInt(net);
}

export function sumSplitPays(rows: PaymentSplitRow[]): number {
  return roundInt(rows.reduce((acc, r) => acc + (Number(r.pay) || 0), 0));
}

export function sumSplitTax(rows: PaymentSplitRow[]): number {
  return roundInt(rows.reduce((acc, r) => acc + (Number(r.tax) || 0), 0));
}

export function sumSplitIncludedTax(rows: PaymentSplitRow[]): number {
  return roundInt(rows.reduce((acc, r) => acc + (Number(r.includedTax) || 0), 0));
}

export function sumSplitLineTotals(rows: PaymentSplitRow[]): number {
  return roundInt(rows.reduce((acc, r) => acc + (Number(r.lineTotal) || 0), 0));
}

/** Grand total the member actually pays: sum of line totals. */
export function calculateSplitGrandTotal(rows: PaymentSplitRow[]): number {
  return sumSplitLineTotals(rows);
}

/**
 * Gross line base for combined receipt Amount (membership `pay`, trainer `totalPaid` reverse).
 * Trainer primary stores package baseAmount, not line pay — never use baseAmount here.
 */
export function getSplitLineGrossPay(fee: {
  pay?: number | string | null;
  totalPaid?: number | string | null;
  discount?: number | string | null;
  tax?: number | string | null;
  taxCalculationType?: string;
}): number {
  if (fee.pay != null && fee.pay !== '') {
    return roundInt(Number(fee.pay) || 0);
  }
  // Trainer booking fees: totalPaid is cash; reverse gross when WITH_TAX.
  if (fee.totalPaid != null && fee.totalPaid !== '') {
    const totalPaid = Number(fee.totalPaid) || 0;
    const tax =
      fee.taxCalculationType === 'WITH_TAX' ? Number(fee.tax) || 0 : 0;
    const discount = Number(fee.discount) || 0;
    return roundInt(totalPaid - tax + discount);
  }
  return 0;
}

/**
 * Amount the customer actually paid on one split line (for receipt display):
 * prefer stored totalPaid; else (gross pay − line discount) + tax when WITH_TAX.
 */
export function getSplitLineAmountPaid(fee: {
  pay?: number | string | null;
  totalPaid?: number | string | null;
  discount?: number | string | null;
  tax?: number | string | null;
  taxCalculationType?: string;
}): number {
  if (fee.totalPaid != null && fee.totalPaid !== '') {
    return roundInt(Number(fee.totalPaid) || 0);
  }
  const pay = Number(fee.pay) || 0;
  const discount = Number(fee.discount) || 0;
  const tax = Number(fee.tax) || 0;
  const net = Math.max(0, pay - discount);
  if (fee.taxCalculationType === 'WITH_TAX') {
    return roundInt(net + tax);
  }
  return roundInt(net);
}

/**
 * LOCKED: what the user must allocate across rows is the NET amount —
 * fund.pay − discount (never below 0).
 */
export function getSplitAllocationTarget(
  fundPay: number,
  totalDiscount: number,
): number {
  const pay = Number(fundPay) || 0;
  const discount = Number(totalDiscount) || 0;
  return roundInt(Math.max(0, pay - discount));
}

export function isSplitAllocationValid(
  rows: PaymentSplitRow[],
  allocationTarget: number,
): boolean {
  if (!rows || rows.length < 2) return false;
  return Math.abs(sumSplitPays(rows) - (Number(allocationTarget) || 0)) <= 0.01;
}

/** Payment modes selected on more than one split row. */
export function getDuplicateSplitPaymentModes(
  rows: PaymentSplitRow[],
): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const row of rows ?? []) {
    const mode = String(row.paymentMode || '').trim();
    if (!mode) {
      continue;
    }
    if (seen.has(mode)) {
      duplicates.add(mode);
    }
    seen.add(mode);
  }
  return Array.from(duplicates);
}

export function hasDuplicateSplitPaymentModes(rows: PaymentSplitRow[]): boolean {
  return getDuplicateSplitPaymentModes(rows).length > 0;
}

/** First branch payment mode not already used on another split row. */
export function getFirstUnusedSplitPaymentMode(
  allModes: string[],
  usedModes: string[],
): string {
  const used = new Set(usedModes.map((m) => String(m || '').trim()).filter(Boolean));
  return allModes.find((mode) => !used.has(String(mode || '').trim())) ?? allModes[0] ?? '';
}

/**
 * Fee Details payment-mode edit: hide modes already used on sibling split rows.
 * Always keeps the current fee's mode in the list.
 */
export function getSplitFeePaymentOptionsForEdit<T extends { value: string }>(
  allOptions: T[],
  feeDetails: any[] | null | undefined,
  currentFee: any,
): T[] {
  const group = getSplitGroupFees(feeDetails, currentFee);
  if (group.length <= 1) {
    return allOptions ?? [];
  }
  const currentId = String(currentFee?._id ?? '');
  const currentMode = String(currentFee?.paymentMode || '').trim();
  const usedByOthers = new Set(
    group
      .filter((row) => String(row?._id ?? '') !== currentId)
      .map((row) => String(row.paymentMode || '').trim())
      .filter(Boolean),
  );
  return (allOptions ?? []).filter((option) => {
    const value = String(option.value || '').trim();
    if (!value) {
      return true;
    }
    if (value === currentMode) {
      return true;
    }
    return !usedByOthers.has(value);
  });
}

/** Hide modes picked on other rows; always keep the current row's selection. */
export function getSplitPaymentOptionsForRow<T extends { value: string }>(
  allOptions: T[],
  rows: PaymentSplitRow[],
  currentRowId: string,
): T[] {
  const currentRow = (rows ?? []).find((row) => row.id === currentRowId);
  const currentMode = String(currentRow?.paymentMode || '').trim();
  const usedByOthers = new Set(
    (rows ?? [])
      .filter((row) => row.id !== currentRowId)
      .map((row) => String(row.paymentMode || '').trim())
      .filter(Boolean),
  );
  return (allOptions ?? []).filter((option) => {
    const value = String(option.value || '').trim();
    if (!value) {
      return true;
    }
    if (value === currentMode) {
      return true;
    }
    return !usedByOthers.has(value);
  });
}

/** Scale one NET row pay back to its GROSS share of fund.pay. */
export function netSplitPayToGross(
  netPay: number | null,
  fundPay: number,
  netTarget: number,
): number {
  if (netTarget <= 0) {
    return Number(netPay) || 0;
  }
  return roundInt(((Number(netPay) || 0) * fundPay) / netTarget);
}

/**
 * Convert NET UI rows to GROSS rows for the API so sum(gross) === fund.pay.
 * The last row absorbs any rounding remainder.
 */
export function convertNetSplitsToGross(
  rows: PaymentSplitRow[],
  fundPay: number,
  totalDiscount: number,
): PaymentSplitRow[] {
  const pay = Number(fundPay) || 0;
  const netTarget = getSplitAllocationTarget(pay, totalDiscount);
  if (!rows.length || pay <= 0) {
    return rows.map((row) => ({ ...row }));
  }
  if (netTarget <= 0) {
    return rows.map((row) => ({ ...row, pay: 0 }));
  }
  let allocatedGross = 0;
  return rows.map((row, index) => {
    const grossPay =
      index === rows.length - 1
        ? roundInt(pay - allocatedGross)
        : netSplitPayToGross(row.pay, pay, netTarget);
    if (index < rows.length - 1) {
      allocatedGross = roundInt(allocatedGross + grossPay);
    }
    return { ...row, pay: grossPay };
  });
}

/** Proportional share of the total discount for one GROSS line pay. */
export function proportionalLineDiscount(
  linePay: number,
  fundPay: number,
  totalDiscount: number,
): number {
  const pay = Number(fundPay) || 0;
  const discount = Number(totalDiscount) || 0;
  if (pay <= 0 || discount <= 0) return 0;
  return roundInt(((Number(linePay) || 0) / pay) * discount);
}

/** Online-/Check- payment modes need a receipt upload in single mode. */
export function needsReceiptUpload(paymentMode: string | undefined): boolean {
  if (!paymentMode) return false;
  return paymentMode.includes('Online-') || paymentMode.includes('Check-');
}

export function normalizeSplitGroupId(id: unknown): string | null {
  if (id == null || id === '') return null;
  if (typeof id === 'object' && id !== null && '$oid' in (id as object)) {
    return String((id as { $oid: string }).$oid);
  }
  return String(id);
}

/** All rows in one split payment group (splitGroupId only), ordered by splitIndex. */
export function getSplitGroupFees(
  feeDetails: any[] | null | undefined,
  fee: any,
): any[] {
  const groupId = normalizeSplitGroupId(fee?.splitGroupId);
  if (!groupId) return [fee];

  const group = (feeDetails || []).filter(
    (row) => normalizeSplitGroupId(row?.splitGroupId) === groupId,
  );
  if (!group.length) return [fee];

  return [...group].sort(
    (a, b) => (Number(a.splitIndex) || 0) - (Number(b.splitIndex) || 0),
  );
}

/**
 * Fee Details chip label, e.g. "Split 1/2".
 * Null when the fee is not part of a multi-row split group.
 * `template` may use `{index}` and `{total}` placeholders.
 */
export function getSplitPartLabel(
  fee: any,
  feeDetails: any[] | null | undefined,
  template = 'Split {index}/{total}',
): string | null {
  const group = getSplitGroupFees(feeDetails, fee);
  if (group.length <= 1) return null;
  let index = Number(fee?.splitIndex) || 0;
  if (!index) {
    const found = group.findIndex(
      (row) => String(row?._id) === String(fee?._id),
    );
    index = found >= 0 ? found + 1 : 1;
  }
  return template
    .replace(/\{index\}/g, String(index))
    .replace(/\{total\}/g, String(group.length));
}

/** Receipt mode-of-payment lines: net paid per method (after discount + tax). */
export function buildSplitPaymentModeLabelFromFees(fees: any[]): string {
  return (fees || [])
    .map((fee) => {
      const amount = getSplitLineAmountPaid(fee);
      return `${fee.paymentMode || ''}: ${amount.toLocaleString()}`;
    })
    .join('<br>');
}

/**
 * Display label for subscription detail Mode column.
 * Split fees show each mode (e.g. "Cash-Reception, Credit/Debit-POS") instead of "Split".
 */
export function formatSubscriptionPaymentModesDisplay(fee: {
  paymentMode?: string;
  isSplitPayment?: boolean;
  splits?: Array<{ paymentMode?: string }>;
}): string {
  const modes = (fee?.splits || [])
    .map((split) => String(split?.paymentMode || '').trim())
    .filter(Boolean);
  if (modes.length >= 2) {
    return modes.join(', ');
  }
  const top = String(fee?.paymentMode || '').trim();
  if (top && top.toLowerCase() !== 'split') {
    return top;
  }
  return modes[0] || top || '';
}

/** Build receipt paymentMode string from subscription fee splits (proportional discount). */
export function buildSubscriptionSplitReceiptPaymentMode(fee: {
  pay?: string | number;
  discount?: string | number;
  paymentMode?: string;
  splits?: Array<{
    paymentMode?: string;
    pay?: string | number;
    tax?: string | number;
    taxType?: string;
  }>;
}): string {
  if (!Array.isArray(fee?.splits) || fee.splits.length < 2) {
    return String(fee?.paymentMode || '');
  }
  const totalPay = Number(fee.pay) || 0;
  const totalDiscount = Number(fee.discount) || 0;
  return buildSplitPaymentModeLabelFromFees(
    fee.splits.map((split) => ({
      paymentMode: split.paymentMode,
      pay: split.pay,
      tax: split.tax,
      discount: proportionalLineDiscount(
        Number(split.pay) || 0,
        totalPay,
        totalDiscount,
      ),
      taxCalculationType: split.taxType,
    })),
  );
}

export interface SplitPaymentModeLine {
  mode: string;
  amountLabel: string;
}

/**
 * Parse "Mode: amount" lines from a receipt paymentMode string
 * (supports plain text or HTML `<br>` separators used on split receipts).
 */
export function parseSplitPaymentModeLines(
  paymentMode: string | undefined | null,
): SplitPaymentModeLine[] {
  if (!paymentMode) return [];
  const normalized = String(paymentMode)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&nbsp;/gi, ' ');
  return normalized
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.lastIndexOf(':');
      if (idx <= 0) return null;
      const mode = line.slice(0, idx).trim();
      const amountLabel = line.slice(idx + 1).trim();
      if (!mode || !amountLabel) return null;
      return { mode, amountLabel };
    })
    .filter((row): row is SplitPaymentModeLine => !!row);
}

/** True when receipt paymentMode encodes 2+ mode:amount lines (split payment). */
export function isMultiPaymentModeReceipt(
  paymentMode: string | undefined | null,
): boolean {
  return parseSplitPaymentModeLines(paymentMode).length >= 2;
}

/** Top Mode of Payment cell: short label for split, raw mode for single. */
export function getReceiptPaymentModeHeader(
  paymentMode: string | undefined | null,
  multipleModesLabel = 'Multiple Payment Modes',
): string {
  return isMultiPaymentModeReceipt(paymentMode)
    ? multipleModesLabel
    : String(paymentMode || '');
}

/** Base invoice number without the split row suffix (-1, -2, …). */
export function getSplitBaseInvoice(fee: { invoice?: string }): string {
  return String(fee?.invoice ?? '').replace(/-\d+$/, '');
}

/** Combined tax for reprinting a split payment from stored fee rows. */
export function getCombinedSplitTaxForPrint(fees: any[]): {
  taxType: string;
  tax: number;
} {
  let withTaxSum = 0;
  let includedTaxSum = 0;
  let hasWithTax = false;
  let hasIncludingTax = false;

  for (const fee of fees || []) {
    const type = fee.taxCalculationType || 'NO_TAX';
    if (type === 'WITH_TAX') {
      hasWithTax = true;
      withTaxSum += Number(fee.tax) || 0;
    } else if (type === 'INCLUDING_TAX') {
      hasIncludingTax = true;
      const pay = getSplitLineGrossPay(fee);
      const discount = Number(fee.discount) || 0;
      const rate = Number(fee.taxRate) || 0;
      if (pay > 0 && discount < pay) {
        includedTaxSum += roundInt(((pay - discount) * rate) / 100);
      }
    }
  }

  if (hasWithTax) return { taxType: 'WITH_TAX', tax: roundInt(withTaxSum) };
  if (hasIncludingTax) {
    return { taxType: 'INCLUDING_TAX', tax: roundInt(includedTaxSum) };
  }
  return { taxType: 'NO_TAX', tax: 0 };
}

/**
 * Build one combined invoice object for printing a split payment group
 * (same totals and payment-mode layout as Add Fee on first save).
 */
export function buildCombinedSplitFundInvoice(fee: any, groupFees: any[]): any {
  const sorted = getSplitGroupFees(groupFees, fee);
  const primary = sorted[0] || fee;
  // Sum line grosses (membership `pay` / trainer totalPaid reverse). Prefer that
  // over splitTotal — trainer used to store sum(totalPaid) in splitTotal.
  const payFromLines = roundInt(
    sorted.reduce((acc, row) => acc + getSplitLineGrossPay(row), 0),
  );
  const pay =
    payFromLines ||
    Number(primary.splitTotal) ||
    0;
  const discount = roundInt(
    sorted.reduce((acc, row) => acc + (Number(row.discount) || 0), 0),
  );
  const { taxType, tax } = getCombinedSplitTaxForPrint(sorted);
  // Partial-payment remaining balance is stored on the first split row only.
  const balanceRow =
    sorted.find((row) => Number(row.splitIndex) === 1) ||
    sorted.find((row) => Number(row.balance) > 0) ||
    primary;

  const srbLines = buildSplitSrbInvoiceLines(sorted);
  const fbrLines = buildSplitFbrInvoiceLines(sorted);
  return {
    ...primary,
    invoice: getSplitBaseInvoice(primary),
    pay,
    discount,
    tax,
    taxCalculationType: taxType,
    paymentMode: buildSplitPaymentModeLabelFromFees(sorted),
    balance: Number(balanceRow.balance) || 0,
    // One SRB ID per payment line for receipt; empty when SRB off / send failed.
    SRBInvoiceLines: srbLines,
    // Single ID only when exactly one line has an SRB id (compat with old templates).
    SRBInvoiceID:
      srbLines.length === 1
        ? (sorted.find((r) => r.SRBInvoiceID)?.SRBInvoiceID ?? primary.SRBInvoiceID)
        : undefined,
    FBRInvoiceLines: fbrLines,
    FBRInvoiceID: fbrLines.length === 1 ? fbrLines[0] : undefined,
  };
}

/** Receipt lines: "PaymentMode: SRB-id" for each split row that has an ID. */
export function buildSplitSrbInvoiceLines(fees: any[]): string[] {
  return (fees || [])
    .filter((fee) => fee?.SRBInvoiceID)
    .map((fee) => {
      const mode = String(fee.paymentMode || '').trim();
      const id = String(fee.SRBInvoiceID).trim();
      return mode ? `${mode}: ${id}` : id;
    });
}

/** One raw FBR invoice number per split row. Blank ids are omitted. */
export function buildSplitFbrInvoiceLines(fees: any[]): string[] {
  return (fees || [])
    .filter((fee) => fee?.FBRInvoiceID)
    .map((fee) => String(fee.FBRInvoiceID).trim())
    .filter((id) => id.length > 0);
}

/** Fee-details print: single row as-is, split row as combined invoice. */
export function prepareFeeInvoiceForPrint(
  fee: any,
  feeDetails: any[] | null | undefined,
): any {
  const groupFees = getSplitGroupFees(feeDetails, fee);
  if (groupFees.length <= 1) {
    return { ...fee };
  }
  return buildCombinedSplitFundInvoice(fee, groupFees);
}

/** User-facing delete confirmation for split vs single fee rows. */
export function getSplitFeeDeleteConfirmationMessage(
  fee: any,
  feeDetails: any[] | null | undefined,
  messages?: { single?: string; split?: string },
): string {
  const group = getSplitGroupFees(feeDetails, fee);
  if (group.length <= 1) {
    return messages?.single || 'Are you sure you want to Delete Fee ?';
  }
  const template =
    messages?.split ||
    'This fee is part of a split payment ({count} payment modes). Deleting it will remove all {count} entries related to this split payment. Continue?';
  return template.replace(/\{count\}/g, String(group.length));
}
